import { Component, OnInit, Input } from '@angular/core';
import { BankAccountService } from '../../../services/bank-account.service';
import { CreditLineService } from '../../../services/credit-line.service';
import { SettingService } from '../../../services/setting.service';
import { ErrorMessageService } from '../../../../shared/error-message.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'app-bank-account-info',
    templateUrl: './bank-account-info.component.html',
    styleUrls: ['./bank-account-info.component.scss']
})
export class BankAccountInfoComponent implements OnInit {

    @Input() loan: any = null;
    @Input() loanId: any = null;
    @Input() canEdit: boolean = false;
    @Input() user: any = null;
    @Input() leadId: any = null;

    bankAccounts: any[] = [];
    selectedBankAccountId: number = null;
    manualIban: string = '';
    isEditing: boolean = false;
    issuingPending: any = null;
    isLoading: boolean = false;
    showFurioButton: boolean = false;

    constructor(
        private bankAccountService: BankAccountService,
        private creditLineService: CreditLineService,
        private settingService: SettingService,
        private errorMessageService: ErrorMessageService,
        private http: HttpClient
    ) { }

    ngOnInit() {
        this.checkFurioSetting();
        this.loadIssuingPending();
    }

    checkFurioSetting() {
        this.settingService.getSettingByName('loan_issuing_bank_account_furio_capital_sl').subscribe(
            (response: any) => {
                if (response && response.value) {
                    this.showFurioButton = true;
                }
            }
        );
    }

    createFurioBankAccount() {
        this.isLoading = true;
        this.settingService.getSettingByName('loan_issuing_bank_account_furio_capital_sl').subscribe(
            (response: any) => {
                if (response && response.value) {
                    const furioIban = this.formatIban(response.value);
                    if (!this.validateIban(furioIban)) {
                        this.isLoading = false;
                        this.errorMessageService.showError('Furio IBAN is not valid');
                        return;
                    }
                    this.bankAccountService.create(this.user.id, this.leadId, furioIban).subscribe(
                        (createResponse: any) => {
                            this.loadBankAccounts();
                            this.selectedBankAccountId = createResponse.id;
                            this.updateIssuingPending(createResponse.id);
                        },
                        error => {
                            this.isLoading = false;
                            this.errorMessageService.showError('Could not create Furio bank account');
                        }
                    );
                } else {
                    this.isLoading = false;
                    this.errorMessageService.showError('Furio IBAN configuration not found');
                }
            },
            error => {
                this.isLoading = false;
                this.errorMessageService.showError('Could not get Furio IBAN configuration');
            }
        );
    }

    loadIssuingPending() {
        if (this.loanId) {
            this.creditLineService.getLoanIssuingPending(this.loanId).subscribe(
                (response: any) => {
                    this.issuingPending = response;
                    if (response && response.bank_account_id) {
                        this.selectedBankAccountId = response.bank_account_id;
                    }
                    if (this.canEdit && this.user && this.user.id) {
                        this.loadBankAccounts();
                    }
                }
            );
        }
    }

    loadBankAccounts() {
        if (this.user && this.user.id) {
            this.bankAccountService.getByUserId(this.user.id).subscribe(
                (accounts: any) => {
                    this.bankAccounts = accounts;
                    if (this.issuingPending && this.issuingPending.bank_account) {
                        this.selectedBankAccountId = this.issuingPending.bank_account.id;
                    }
                }
            );
        }
    }

    formatIban(iban: string): string {
        return iban.replace(/[\s-]/g, '').toUpperCase();
    }

    validateIban(iban: string): boolean {
        const cleaned = this.formatIban(iban);
        return /^ES\d{22}$/.test(cleaned);
    }

    onIbanInput(event: any) {
        this.manualIban = event.target.value;
        if (this.validateIban(this.manualIban)) {
            this.selectedBankAccountId = null;
        }
    }

    onSelectChange(event: any) {
        if (event) {
            this.manualIban = '';
        }
    }

    saveBankAccount() {
        this.isLoading = true;

        if (this.manualIban && this.validateIban(this.manualIban) && this.user && this.user.id) {
            const formattedIban = this.formatIban(this.manualIban);
            this.bankAccountService.create(this.user.id, this.leadId, formattedIban).subscribe(
                (response: any) => {
                    this.loadBankAccounts();
                    this.selectedBankAccountId = response.id;
                    this.updateIssuingPending(response.id);
                },
                error => {
                    this.isLoading = false;
                    this.errorMessageService.showError('Could not create bank account');
                }
            );
        } else if (this.selectedBankAccountId) {
            this.updateIssuingPending(this.selectedBankAccountId);
        } else {
            this.isLoading = false;
            this.errorMessageService.showError('Select an account or enter a valid IBAN');
        }
    }

    updateIssuingPending(bankAccountId: number) {
        if (this.loanId && this.loanId) {
            this.creditLineService.updateIssuingPending(this.loanId, bankAccountId).subscribe(
                (response: any) => {
                    this.issuingPending = response;
                    this.errorMessageService.showSuccess('Bank account has been changed');
                    this.isEditing = false;
                    this.manualIban = '';
                    this.isLoading = false;
                },
                error => {
                    this.isLoading = false;
                    this.errorMessageService.showError('Could not change bank account');
                }
            );
        }
    }

    cancelEdit() {
        this.isEditing = false;
        this.manualIban = '';
        if (this.issuingPending && this.issuingPending.bank_account_id) {
            this.selectedBankAccountId = this.issuingPending.bank_account_id;
        }
    }
}
